import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { entityKindSchema, entityEdgeKindSchema } from './entities/index.ts';

const c = initContract();

const errorSchema = z.object({ message: z.string() });

export const entityIdSchema = z.string().regex(/^ent_[0-9A-Za-z]{22}$/);

export const entityStatusValues = ['active', 'archived'] as const;
export const entityStatusSchema = z.enum(entityStatusValues);

export const entityListObjectSchema = z.object({
  id: entityIdSchema,
  kind: entityKindSchema,
  slug: z.string(),
  name: z.string(),
  blurb: z.string().nullable(),
  status: entityStatusSchema,
  lifecycle: z.string().nullable(),
  human_edited_at: z.string().datetime().nullable(),
  last_active_at: z.string().datetime(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const entityAttachmentSchema = z.object({
  thread_id: z.string(),
  thread_title: z.string(),
  role: z.string(),
  score: z.number().nullable(),
  added_at: z.string().datetime(),
  added_by: z.object({
    id: z.string(),
    display_name: z.string(),
  }),
});

export const entityEdgeRefSchema = z.object({
  entity_id: entityIdSchema,
  name: z.string(),
  slug: z.string(),
  kind: entityKindSchema,
  edge_kind: entityEdgeKindSchema,
});

export const entityDetailSchema = entityListObjectSchema.extend({
  payload: z.record(z.string(), z.unknown()),
  attachments: z.array(entityAttachmentSchema),
  edges: z.object({
    outgoing: z.array(entityEdgeRefSchema),
    incoming: z.array(entityEdgeRefSchema),
  }),
});

export const entityListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('entities'),
  has_more: z.boolean(),
  objects: z.array(entityListObjectSchema),
});

export const listEntitiesQuerySchema = z.object({
  kind: entityKindSchema.optional().describe('Filter to a single entity kind'),
  status: entityStatusSchema.optional().describe('Filter by entity status'),
  after: entityIdSchema.optional().describe('Cursor: return entities after this id'),
  before: entityIdSchema.optional().describe('Cursor: return entities before this id'),
});

export const patchEntityRequestSchema = z.object({
  name: z.string().min(1).optional().describe('New display name for the entity'),
  blurb: z.string().nullable().optional().describe('New blurb; null clears it'),
  payload: z
    .record(z.string(), z.unknown())
    .optional()
    .describe('Replacement payload for the entity'),
});

export const attachEntityThreadRequestSchema = z.object({
  thread_id: z.string().min(1).describe('Id of the thread to attach'),
  role: z.string().min(1).describe('Role of the thread relative to the entity'),
  score: z
    .number()
    .min(0)
    .max(1)
    .nullable()
    .optional()
    .describe('Optional relevance score in [0, 1]'),
});

export const addEntityEdgeRequestSchema = z.object({
  to_id: entityIdSchema.describe('Id of the target entity for the edge'),
  kind: entityEdgeKindSchema.describe('Kind of edge to create'),
});

export const entitiesContract = c.router({
  listEntities: {
    method: 'GET',
    path: '/entities',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: listEntitiesQuerySchema,
    responses: {
      200: entityListResponseSchema,
      401: errorSchema,
    },
    summary: 'List entities in the authenticated user’s organization',
  },
  getEntity: {
    method: 'GET',
    path: '/entities/:entityId',
    pathParams: z.object({
      entityId: entityIdSchema.describe('Id of the entity to fetch'),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: entityDetailSchema,
      401: errorSchema,
      404: errorSchema,
    },
    summary: 'Get an entity with its payload, attachments, and edges',
  },
  patchEntity: {
    method: 'PATCH',
    path: '/entities/:entityId',
    pathParams: z.object({
      entityId: entityIdSchema.describe('Id of the entity to update'),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: patchEntityRequestSchema,
    responses: {
      200: entityDetailSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Update an entity’s name, blurb, or payload',
  },
  archiveEntity: {
    method: 'POST',
    path: '/entities/:entityId/archive',
    pathParams: z.object({
      entityId: entityIdSchema.describe('Id of the entity to archive'),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}),
    responses: {
      200: entityDetailSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Archive an entity',
  },
  attachEntityThread: {
    method: 'POST',
    path: '/entities/:entityId/threads',
    pathParams: z.object({
      entityId: entityIdSchema.describe('Id of the entity to attach a thread to'),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: attachEntityThreadRequestSchema,
    responses: {
      200: entityDetailSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Attach a thread to an entity',
  },
  detachEntityThread: {
    method: 'DELETE',
    path: '/entities/:entityId/threads/:threadId',
    pathParams: z.object({
      entityId: entityIdSchema.describe('Id of the entity to detach a thread from'),
      threadId: z.string().min(1).describe('Id of the thread to detach'),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: z.object({
      role: z.string().min(1).describe('Role of the thread membership to remove'),
    }),
    responses: {
      200: entityDetailSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Detach a thread from an entity',
  },
  addEntityEdge: {
    method: 'POST',
    path: '/entities/:entityId/edges',
    pathParams: z.object({
      entityId: entityIdSchema.describe('Id of the source entity for the edge'),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: addEntityEdgeRequestSchema,
    responses: {
      200: entityDetailSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Add an edge from this entity to another entity',
  },
  removeEntityEdge: {
    method: 'DELETE',
    path: '/entities/:entityId/edges/:toId/:kind',
    pathParams: z.object({
      entityId: entityIdSchema.describe('Id of the source entity for the edge'),
      toId: entityIdSchema.describe('Id of the target entity for the edge'),
      kind: entityEdgeKindSchema.describe('Kind of edge to remove'),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: entityDetailSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Remove an edge from this entity to another entity',
  },
});

export type EntityStatus = z.infer<typeof entityStatusSchema>;
export type EntityListObject = z.infer<typeof entityListObjectSchema>;
export type EntityAttachment = z.infer<typeof entityAttachmentSchema>;
export type EntityEdgeRef = z.infer<typeof entityEdgeRefSchema>;
export type EntityDetail = z.infer<typeof entityDetailSchema>;
export type EntityListResponse = z.infer<typeof entityListResponseSchema>;
export type ListEntitiesQuery = z.infer<typeof listEntitiesQuerySchema>;
export type PatchEntityRequest = z.infer<typeof patchEntityRequestSchema>;
export type AttachEntityThreadRequest = z.infer<typeof attachEntityThreadRequestSchema>;
export type AddEntityEdgeRequest = z.infer<typeof addEntityEdgeRequestSchema>;
